import React, { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import PDFHeader from './PDFHeader';
import { ItineraryItem, TripInfo } from '../../types/Quotation';
import { parseDateValue } from '../../utils/date';
import FlightCard from './FlightCard';
import HotelCard from './HotelCard';
import ActivityCard from './ActivityCard';
import TransferCard from './TransferCard';
import {
  buildItineraryPageEntries,
  DEFAULT_ITINERARY_PAGINATION,
  paginateItineraryEntries,
} from './itineraryPagination';

interface PDFPage3Props {
  itinerary: ItineraryItem[];
  itineraryImages: string[];
  startDate: string;
  tripInfo: TripInfo;
  selectedRoomCount?: number;
  renderedAt?: string;
}

const formatLongDate = (d: Date) =>
  d.toLocaleDateString('en-GB', {
    weekday: 'short',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });

const DAY_MS = 86400000;

type MeasuredItineraryLayout = {
  availableHeight: number;
  firstPageTitleHeight: number;
  continuationTitleHeight: number;
  imageHeights: number[];
  itemHeights: Record<string, number>;
};

const PDFPage3: React.FC<PDFPage3Props> = ({
  itinerary,
  itineraryImages,
  startDate,
  tripInfo,
  selectedRoomCount,
  renderedAt,
}) => {
  const pageRef = useRef<HTMLDivElement | null>(null);
  const firstSectionRef = useRef<HTMLElement | null>(null);
  const titleRef = useRef<HTMLHeadingElement | null>(null);
  const imageRefs = useRef<(HTMLImageElement | null)[]>([]);
  const measurementRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const [measuredLayout, setMeasuredLayout] = useState<MeasuredItineraryLayout | null>(null);
  const base = parseDateValue(startDate);
  const itineraryEntries = useMemo(() => buildItineraryPageEntries(itinerary), [itinerary]);
  const placeBoundaries = tripInfo.places.reduce<{ start: number; end: number; idx: number }[]>((acc, p, idx) => {
    const nights = Math.max(1, Number(p.nights || 0));
    const start = acc.length === 0 ? 0 : acc[acc.length - 1].end + 1;
    const end = start + nights - 1;
    acc.push({ start, end, idx });
    return acc;
  }, []);

  const resolvePlaceLabel = (dayIndex: number, fallback?: string) => {
    if (tripInfo.places.length === 0) return fallback || 'Location';
    const match = placeBoundaries.find((b) => dayIndex >= b.start && dayIndex <= b.end);
    const placeIdx = match ? match.idx : tripInfo.places.length - 1;
    const place = tripInfo.places[placeIdx];
    return place?.label || place?.value || fallback || 'Location';
  };

  useLayoutEffect(() => {
    if (itineraryEntries.length === 0 && itineraryImages.length === 0) {
      setMeasuredLayout(null);
      return;
    }

    const pageRect = pageRef.current?.getBoundingClientRect();
    const sectionRect = firstSectionRef.current?.getBoundingClientRect();
    const titleRect = titleRef.current?.getBoundingClientRect();
    const nextImageHeights = imageRefs.current
      .map((node) => {
        const rect = node?.getBoundingClientRect();
        return rect && rect.height > 0 ? Math.ceil(rect.height + 12) : 0;
      })
      .filter((height) => height > 0);
    const nextItemHeights = itineraryEntries.reduce<Record<string, number>>((acc, item) => {
      const rect = measurementRefs.current[item.key]?.getBoundingClientRect();
      if (rect && rect.height > 0) {
        acc[item.key] = Math.ceil(rect.height);
      }
      return acc;
    }, {});

    const nextLayout: MeasuredItineraryLayout = {
      availableHeight:
        pageRect && sectionRect
          ? (() => {
            const measuredHeight = Math.floor(pageRect.bottom - sectionRect.top - 24);
            return measuredHeight > 0 ? measuredHeight : DEFAULT_ITINERARY_PAGINATION.availableHeight;
          })()
          : DEFAULT_ITINERARY_PAGINATION.availableHeight,
      firstPageTitleHeight:
        titleRect && titleRect.height > 0
          ? Math.ceil(titleRect.height + 24)
          : DEFAULT_ITINERARY_PAGINATION.firstPageTitleHeight,
      continuationTitleHeight:
        titleRect && titleRect.height > 0
          ? Math.ceil(titleRect.height + 24)
          : DEFAULT_ITINERARY_PAGINATION.continuationTitleHeight,
      imageHeights: nextImageHeights,
      itemHeights: nextItemHeights,
    };

    setMeasuredLayout((current) => {
      const currentKey = JSON.stringify(current);
      const nextKey = JSON.stringify(nextLayout);
      return currentKey === nextKey ? current : nextLayout;
    });
  }, [itineraryEntries, itineraryImages.length]);

  const itineraryPages = useMemo(
    () =>
      paginateItineraryEntries(itineraryEntries, {
        ...(measuredLayout || {}),
      }),
    [itineraryEntries, measuredLayout]
  );

  const renderDay = (day: ItineraryItem, idx: number) => {
    const dayDate = base ? new Date(base.getTime() + (day.day - 1) * DAY_MS) : new Date(NaN);
    const hotelsForDay = day.hotels || [];
    const hasHotels = hotelsForDay.length > 0;
    const points = Array.isArray((day as any).points) ? (day as any).points.filter(Boolean) : [];
    const dayTitle = day.title || '';
    const placeLabel = resolvePlaceLabel(day.day - 1, day.place?.label || day.place?.value || dayTitle);

    return (
      <div className="day-itinerary-container pdf-keep-together" key={`day-${day.day}-${idx}`}>
        <div className="day-header">
          <h3>Day {day.day}</h3>
          <span className="day-date">{Number.isNaN(dayDate.getTime()) ? '' : formatLongDate(dayDate)}</span>
        </div>

        {(dayTitle || points.length > 0) && (
          <div className="day-summary">
            {dayTitle && <div className="day-title">{dayTitle}</div>}
            {points.length > 0 && (
              <ul className="day-points">
                {points.map((point: string, pointIdx: number) => (
                  <li key={`${day.day}-point-${pointIdx}`}>{point}</li>
                ))}
              </ul>
            )}
          </div>
        )}

        {day.transfers && day.transfers.length > 0 && (
          <div className="pdf-section">
            <h4>Transfers</h4>
            {day.transfers.map((transfer, transferIndex) => (
              <TransferCard key={`${idx}-transfer-${transferIndex}`} transfer={{ ...transfer, day: day.day }} />
            ))}
          </div>
        )}

        {hasHotels && (
          <div className="pdf-section">
            <h4>Hotel at {placeLabel}</h4>
            {hotelsForDay.map((hotel, hotelIndex) => (
              <HotelCard key={`${idx}-hotel-${hotelIndex}`} hotel={{ ...hotel, day: day.day }} selectedRoomCount={selectedRoomCount} />
            ))}
          </div>
        )}

        {day.activities && day.activities.length > 0 && (
          <div className="pdf-section activity-section">
            <h4>Activities</h4>
            {day.activities.map((activity, activityIndex) => (
              <div key={`${idx}-activity-${activityIndex}`} className="activity-item">
                <ActivityCard activity={{ ...activity, day: day.day }} />
              </div>
            ))}
          </div>
        )}

        {day.flights && day.flights.length > 0 && (
          <div className="pdf-section">
            <h4>Flights</h4>
            {day.flights.map((flight, flightIndex) => (
              <FlightCard key={`${idx}-flight-${flightIndex}`} flight={{ ...flight, day: day.day }} />
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      {(itineraryEntries.length > 0 || itineraryImages.length > 0) && typeof document !== 'undefined' && createPortal(
        <div className="pdf-measurement-root" aria-hidden="true">
          <div className="pdf-page pdf-page--measurement" ref={pageRef}>
            <PDFHeader renderedAt={renderedAt} />
            <section className="pdf-section" ref={firstSectionRef}>
              <h2 ref={titleRef}>🗓️ Day Wise Details</h2>
              {itineraryImages.map((src, index) => (
                <img
                  key={`measure-image-${index}`}
                  ref={(node) => {
                    imageRefs.current[index] = node;
                  }}
                  src={src}
                  alt=""
                  className="itinerary-image"
                />
              ))}
              {itineraryEntries.map((entry, index) => (
                <div
                  key={`measure-${entry.key}`}
                  ref={(node) => {
                    measurementRefs.current[entry.key] = node;
                  }}
                >
                  {renderDay(entry.day, index)}
                </div>
              ))}
            </section>
          </div>
        </div>,
        document.body
      )}

      {itineraryPages.map((page, pageIndex) => (
        <div className="pdf-page" key={`itinerary-page-${pageIndex}`}>
          <PDFHeader renderedAt={renderedAt} />

          <section className="pdf-section">
            {pageIndex === 0 && <h2>🗓️ Day Wise Details</h2>}

            {page.includeImages && itineraryImages.map((src, i) => (
              <img
                key={i}
                src={src}
                alt={`itinerary-${i}`}
                className="itinerary-image"
              />
            ))}

            {page.items.map((entry, index) => renderDay(entry.day, index))}
          </section>
        </div>
      ))}
    </>
  );
};

export default PDFPage3;
